import React, { useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Grid, List, ListItem, ListItemIcon, ListItemText, Button, AppBar, Toolbar, Link, Stack, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudIcon from '@mui/icons-material/Cloud';
import ChatIcon from '@mui/icons-material/Chat';
import TimelineIcon from '@mui/icons-material/Timeline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BoltIcon from '@mui/icons-material/Bolt';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  PhoneAndroid as DevicesIcon,
  Storage as InventoryIcon,
  Tv as OndemandVideoIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

// Import images
import logo0 from '../assets/logo0.png';
import premium8 from '../assets/premium8.png';
import businessImage from '../assets/business.png';
import logoImage from '../assets/logo0.png';
import premium1 from '../assets/premium1.png';

// Keep your existing styled components
const StyledSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: '#f5f5f5',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
  },
}));

const PolicyLinks = styled(Box)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  padding: theme.spacing(1),
  textAlign: 'center',
}));

const PolicyLink = styled('a')(({ theme }) => ({
  color: '#ffffff',
  textDecoration: 'none',
  margin: '0 15px',
  fontSize: '0.875rem',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StrongHeader = styled(Typography)(({ theme }) => ({
  fontFamily: '"Roboto", sans-serif',
  fontWeight: 900,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  fontSize: '3.5rem',
  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
  animation: `${fadeIn} 1s ease-out`,
  '@media (max-width: 600px)': {
    fontSize: '2.5rem',
  },
}));

// Create styled components for background elements
const BackgroundElement = styled('div')({
  position: 'absolute',
  zIndex: 0,
});

const DiagonalStripe = styled(BackgroundElement)({
  width: '100%',
  height: '15px',
  background: '#FFD600',
  transform: 'skewY(-2deg)',
  top: '45%',
});

const BlackCircle = styled(BackgroundElement)({
  width: '180px',
  height: '180px',
  borderRadius: '0',
  background: 'rgba(0, 0, 0, 0.8)',
  right: '5%',
  top: '15%',
});

const YellowSquare = styled(BackgroundElement)({
  width: '120px',
  height: '120px',
  background: '#FFD600',
  left: '10%',
  bottom: '15%',
});

// Top navigation component
const TopNavigation = () => (
  <AppBar position="static" sx={{ bgcolor: '#000', boxShadow: 'none' }}>
    <Toolbar sx={{ justifyContent: 'flex-end', minHeight: '40px' }}>
      <Link href="#" color="inherit" sx={{ mx: 1, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
        Privacy Policy
      </Link>
      <Link href="#" color="inherit" sx={{ mx: 1, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
        Terms of Service
      </Link>
      <Link href="#" color="inherit" sx={{ mx: 1, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
        Cookie Policy
      </Link>
      <Link href="#" color="inherit" sx={{ mx: 1, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
        Accessibility
      </Link>
    </Toolbar>
  </AppBar>
);

// The ServicesSection component with tabs functionality
const ServicesSection = () => {
  const [activeTab, setActiveTab] = useState('FAULT DETECTION');

  return (
    <>
      {/* Services Section with improved stylish background */}
      <Box sx={{ 
        position: 'relative',
        py: 6,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle, #00C853 1px, transparent 1px), radial-gradient(circle, #00C853 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          backgroundPosition: '0 0, 20px 20px',
          opacity: 0.1,
          zIndex: 0,
        }
      }}>
        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'linear-gradient(45deg, #00C853 0%, #69F0AE 100%)',
          borderRadius: '50%',
          opacity: 0.1,
          zIndex: 0,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '150px',
          height: '150px',
          background: 'linear-gradient(45deg, #00C853 0%, #69F0AE 100%)',
          borderRadius: '50%',
          opacity: 0.1,
          zIndex: 0,
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Logo at top left */}
          <Box 
            component="img" 
            src={logo0} 
            alt="Warrili Logo" 
            sx={{ 
              height: '40px', 
              mb: 4
            }} 
          />

          {/* Title */}
          <Typography variant="h3" component="h1" sx={{ 
            fontSize: '2.5rem',
            fontWeight: 700,
            color: '#000',
            mb: 4
          }}>
            REPAIR MADE SIMPLE
          </Typography>

          {/* Tab buttons with stylish background */}
          <Box sx={{ 
            display: 'flex',
            flexWrap: 'wrap',
            mb: 3,
            p: 2,
            backgroundColor: 'rgba(255,255,255,0.8)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: '2px',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              background: 'linear-gradient(to bottom, #00C853, #69F0AE)',
            }
          }}>
            <Button 
              onClick={() => setActiveTab('FAULT DETECTION')}
              sx={{ 
                bgcolor: activeTab === 'FAULT DETECTION' ? '#000' : '#fff',
                color: activeTab === 'FAULT DETECTION' ? '#fff' : '#000',
                mr: 1,
                mb: 1,
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: '0.8rem',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: activeTab === 'FAULT DETECTION' ? '#000' : '#f0f0f0' }
              }}
            >
              FAULT DETECTION
            </Button>
            <Button 
              onClick={() => setActiveTab('EXPERT REPAIRS')}
              sx={{ 
                bgcolor: activeTab === 'EXPERT REPAIRS' ? '#000' : '#fff',
                color: activeTab === 'EXPERT REPAIRS' ? '#fff' : '#000',
                mr: 1,
                mb: 1,
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: '0.8rem',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: activeTab === 'EXPERT REPAIRS' ? '#000' : '#f0f0f0' }
              }}
            >
              EXPERT REPAIRS
            </Button>
            <Button 
              onClick={() => setActiveTab('SPARE PARTS')}
              sx={{ 
                bgcolor: activeTab === 'SPARE PARTS' ? '#000' : '#fff',
                color: activeTab === 'SPARE PARTS' ? '#fff' : '#000',
                mr: 1,
                mb: 1,
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: '0.8rem',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: activeTab === 'SPARE PARTS' ? '#000' : '#f0f0f0' }
              }}
            >
              SPARE PARTS
            </Button>
            <Button 
              onClick={() => setActiveTab('VIDEO GUIDES')}
              sx={{ 
                bgcolor: activeTab === 'VIDEO GUIDES' ? '#000' : '#fff',
                color: activeTab === 'VIDEO GUIDES' ? '#fff' : '#000',
                mr: 1,
                mb: 1,
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: '0.8rem',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: activeTab === 'VIDEO GUIDES' ? '#000' : '#f0f0f0' }
              }}
            >
              VIDEO GUIDES
            </Button>
            <Button 
              onClick={() => setActiveTab('CLOUD SERVICES')}
              sx={{ 
                bgcolor: activeTab === 'CLOUD SERVICES' ? '#000' : '#fff',
                color: activeTab === 'CLOUD SERVICES' ? '#fff' : '#000',
                mr: 1,
                mb: 1,
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: '0.8rem',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: activeTab === 'CLOUD SERVICES' ? '#000' : '#f0f0f0' }
              }}
            >
              CLOUD SERVICES
            </Button>
            <Button 
              onClick={() => setActiveTab('SUPPORT')}
              sx={{ 
                bgcolor: activeTab === 'SUPPORT' ? '#000' : '#fff',
                color: activeTab === 'SUPPORT' ? '#fff' : '#000',
                mr: 1,
                mb: 1,
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: '0.8rem',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: activeTab === 'SUPPORT' ? '#000' : '#f0f0f0' }
              }}
            >
              SUPPORT
            </Button>
          </Box>

          {/* Tab Content */}
          <Grid container spacing={4}>
            {/* Left side content - changes based on active tab */}
            <Grid item xs={12} md={7}>
              {/* FAULT DETECTION Content */}
              {activeTab === 'FAULT DETECTION' && (
                <>
                  <Box sx={{ 
                    bgcolor: '#000', 
                    color: '#fff', 
                    p: 3, 
                    mb: 3,
                  }}>
                    <Typography variant="body1">
                      Step-by-step guide on using AI fault detection to identify device issues, 
                      connect with repair experts, and find compatible spare parts - all 
                      powered by intelligent diagnostics.
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    Our AI system guides you through fault detection with precision, connects 
                    you to certified repair professionals, and helps you source genuine spare 
                    parts for your devices.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
                    <Box sx={{ mr: 6, mb: 2 }}>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        15K+
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Repairs Completed
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        98%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Success Rate
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      border: '1px solid #000', 
                      color: '#000',
                      fontWeight: 600
                    }}
                  >
                    EXPLORE PROCESS
                  </Button>
                </>
              )}
              
              {/* EXPERT REPAIRS Content */}
              {activeTab === 'EXPERT REPAIRS' && (
                <>
                  <Box sx={{ 
                    bgcolor: '#000', 
                    color: '#fff', 
                    p: 3, 
                    mb: 3,
                  }}>
                    <Typography variant="body1">
                      Connect with professional repair experts who specialize in all major appliance brands.
                      Get certified technicians with years of experience directly to your doorstep.
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    Our network of experts provides fast, reliable repairs with quality guarantees.
                    All technicians are vetted, certified, and trained to handle complex repairs efficiently.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
                    <Box sx={{ mr: 6, mb: 2 }}>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        500+
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Certified Experts
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        4.9
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Rating Average
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      border: '1px solid #000', 
                      color: '#000',
                      fontWeight: 600
                    }}
                  >
                    FIND AN EXPERT
                  </Button>
                </>
              )}
              
              {/* SPARE PARTS Content */}
              {activeTab === 'SPARE PARTS' && (
                <>
                  <Box sx={{ 
                    bgcolor: '#000', 
                    color: '#fff', 
                    p: 3, 
                    mb: 3,
                  }}>
                    <Typography variant="body1">
                      Browse our extensive catalog of genuine spare parts for all major appliance brands.
                      Find exactly what you need with our AI-powered part matching system.
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    We stock thousands of OEM and compatible parts for refrigerators, washing machines, 
                    dryers, dishwashers, ovens, and more. All parts come with compatibility guarantee.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
                    <Box sx={{ mr: 6, mb: 2 }}>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        10K+
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Parts In Stock
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        24h
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fast Delivery
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      border: '1px solid #000', 
                      color: '#000',
                      fontWeight: 600
                    }}
                  >
                    BROWSE CATALOG
                  </Button>
                </>
              )}
              
              {/* VIDEO GUIDES Content */}
              {activeTab === 'VIDEO GUIDES' && (
                <>
                  <Box sx={{ 
                    bgcolor: '#000', 
                    color: '#fff', 
                    p: 3, 
                    mb: 3,
                  }}>
                    <Typography variant="body1">
                      Watch step-by-step video tutorials for common repairs and maintenance tasks.
                      Our visual guides make DIY repairs accessible and straightforward.
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    From simple maintenance to complex fixes, our video library covers everything
                    you need to know. Each guide is created by experts with clear instructions.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
                    <Box sx={{ mr: 6, mb: 2 }}>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        200+
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Video Tutorials
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        4.8
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        User Rating
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      border: '1px solid #000', 
                      color: '#000',
                      fontWeight: 600
                    }}
                  >
                    WATCH TUTORIALS
                  </Button>
                </>
              )}
              
              {/* CLOUD SERVICES Content */}
              {activeTab === 'CLOUD SERVICES' && (
                <>
                  <Box sx={{ 
                    bgcolor: '#000', 
                    color: '#fff', 
                    p: 3, 
                    mb: 3,
                  }}>
                    <Typography variant="body1">
                      Monitor your appliances remotely with our cloud-based diagnostic services.
                      Get real-time alerts and preventative maintenance recommendations.
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    Our cloud platform connects to compatible smart appliances to track performance,
                    predict potential failures, and optimize energy consumption automatically.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
                    <Box sx={{ mr: 6, mb: 2 }}>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        60%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fewer Breakdowns
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        25%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Energy Savings
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      border: '1px solid #000', 
                      color: '#000',
                      fontWeight: 600
                    }}
                  >
                    CONNECT DEVICES
                  </Button>
                </>
              )}
              
              {/* SUPPORT Content */}
              {activeTab === 'SUPPORT' && (
                <>
                  <Box sx={{ 
                    bgcolor: '#000', 
                    color: '#fff', 
                    p: 3, 
                    mb: 3,
                  }}>
                    <Typography variant="body1">
                      Get 24/7 customer support from our technical team. We're here to help with
                      any questions, troubleshooting, or warranty claims you might have.
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    Our customer support specialists are trained technicians who understand appliances
                    inside and out. Get expert advice through chat, phone, or email support channels.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
                    <Box sx={{ mr: 6, mb: 2 }}>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        24/7
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Available Support
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                        2min
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Average Response
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      border: '1px solid #000', 
                      color: '#000',
                      fontWeight: 600
                    }}
                  >
                    CONTACT SUPPORT
                  </Button>
                </>
              )}
            </Grid>
            
            {/* Right side - Process and Watch Guide */}
            <Grid item xs={12} md={5}>
              <Box sx={{ 
                bgcolor: '#fff', 
                p: 3, 
                borderRadius: 1,
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                width: '100%'
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  3-Step Process:
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
                  <Box sx={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: '#00C853', 
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    1
                  </Box>
                  <Typography>
                    Detect faults with AI
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: '#00C853', 
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    2
                  </Box>
                  <Typography>
                    Get expert repair options
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <Box sx={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: '#00C853', 
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    3
                  </Box>
                  <Typography>
                    Purchase genuine parts
                  </Typography>
                </Box>
                
                {/* Center the image that's shown in the screenshot */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  my: 3,
                  width: '100%'
                }}>
                  <Box 
                    component="img" 
                    src="/assets/ai-repair-illustration.png" 
                    alt="AI Repair Illustration"
                    sx={{ 
                      maxWidth: '100%', 
                      maxHeight: '150px',
                      opacity: 0.9
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 4,
                }}>
                  <Button 
                    variant="contained" 
                    startIcon={<PlayArrowIcon />}
                    sx={{ 
                      bgcolor: '#00C853', 
                      color: '#fff',
                      '&:hover': {
                        bgcolor: '#009624'
                      }
                    }}
                  >
                    WATCH GUIDE
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features section to showcase marketplace */}
      <Box sx={{
        py: 8,
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
      }}>
        <Container>
          <Typography variant="h4" component="h2" align="center" sx={{ 
            fontWeight: 700,
            mb: 1,
            color: '#000'
          }}>
            OUR MARKETPLACE FEATURES
          </Typography>
          
          <Typography variant="subtitle1" align="center" sx={{ 
            mb: 6,
            color: '#666',
            maxWidth: '700px',
            mx: 'auto'
          }}>
            Discover the complete ecosystem of repair services and solutions available on our platform
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 3, 
                height: '100%',
                bgcolor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DevicesIcon sx={{ color: '#00C853', fontSize: '2rem', mr: 2 }} />
                  <Typography variant="h6" component="h3" fontWeight={600}>
                    Fault Detection
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Advanced AI-powered diagnostic tools that quickly identify issues with your devices.
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ 
                    mt: 2, 
                    borderColor: '#00C853', 
                    color: '#00C853',
                    '&:hover': {
                      borderColor: '#00C853',
                      bgcolor: 'rgba(0,200,83,0.05)',
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 3, 
                height: '100%',
                bgcolor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BuildIcon sx={{ color: '#00C853', fontSize: '2rem', mr: 2 }} />
                  <Typography variant="h6" component="h3" fontWeight={600}>
                    Expert Repairs
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Connect with certified technicians for professional repair services with guaranteed results.
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ 
                    mt: 2, 
                    borderColor: '#00C853', 
                    color: '#00C853',
                    '&:hover': {
                      borderColor: '#00C853',
                      bgcolor: 'rgba(0,200,83,0.05)',
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 3, 
                height: '100%',
                bgcolor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InventoryIcon sx={{ color: '#00C853', fontSize: '2rem', mr: 2 }} />
                  <Typography variant="h6" component="h3" fontWeight={600}>
                    Spare Parts
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Access to genuine spare parts and components for all major brands and models.
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ 
                    mt: 2, 
                    borderColor: '#00C853', 
                    color: '#00C853',
                    '&:hover': {
                      borderColor: '#00C853',
                      bgcolor: 'rgba(0,200,83,0.05)',
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 3, 
                height: '100%',
                bgcolor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <OndemandVideoIcon sx={{ color: '#00C853', fontSize: '2rem', mr: 2 }} />
                  <Typography variant="h6" component="h3" fontWeight={600}>
                    Video Guides
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Step-by-step video tutorials for DIY repairs and maintenance of your devices.
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ 
                    mt: 2, 
                    borderColor: '#00C853', 
                    color: '#00C853',
                    '&:hover': {
                      borderColor: '#00C853',
                      bgcolor: 'rgba(0,200,83,0.05)',
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 3, 
                height: '100%',
                bgcolor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CloudIcon sx={{ color: '#00C853', fontSize: '2rem', mr: 2 }} />
                  <Typography variant="h6" component="h3" fontWeight={600}>
                    Cloud Services
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Secure cloud storage for device data, settings, and backup solutions.
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ 
                    mt: 2, 
                    borderColor: '#00C853', 
                    color: '#00C853',
                    '&:hover': {
                      borderColor: '#00C853',
                      bgcolor: 'rgba(0,200,83,0.05)',
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ 
                p: 3, 
                height: '100%',
                bgcolor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SupportAgentIcon sx={{ color: '#00C853', fontSize: '2rem', mr: 2 }} />
                  <Typography variant="h6" component="h3" fontWeight={600}>
                    Support
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  24/7 customer support and technical assistance for all your device issues.
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ 
                    mt: 2, 
                    borderColor: '#00C853', 
                    color: '#00C853',
                    '&:hover': {
                      borderColor: '#00C853',
                      bgcolor: 'rgba(0,200,83,0.05)',
                    }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Green section from the screenshot */}
      <Box sx={{ 
        backgroundColor: '#4CAF50', 
        py: 8,
        textAlign: 'center',
        color: 'white'
      }}>
        <Container>
          <Typography variant="h2" component="h2" sx={{ 
            fontWeight: 600,
            mb: 3
          }}>
            Ready to Solve Your Technical Challenges?
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}>
            Book a consultation with our experts today and take the first step toward efficient, effective solutions
          </Typography>
          
          <Button 
            variant="contained" 
            sx={{ 
              bgcolor: 'white', 
              color: '#4CAF50', 
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)',
              }
            }}
          >
            BOOK NOW
          </Button>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <CallToActionSection />
    </>
  );
};

// Call to Action Section Component
const CallToActionSection = () => {
  return (
    <Box sx={{
      bgcolor: '#4CAF50', // Green background
      py: 6,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" sx={{
              color: 'white',
              fontWeight: 700,
              mb: 2,
            }}>
              Ready to Solve Your Technical Challenges?
            </Typography>
            
            <Typography variant="h6" sx={{
              color: 'white',
              mb: 4,
              opacity: 0.9,
              textAlign: { xs: 'center', md: 'left' },
            }}>
              Book a consultation with our experts today and take the first step toward efficient, effective solutions
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Button 
                variant="contained" 
                sx={{
                  bgcolor: 'white',
                  color: '#4CAF50',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                }}
                component={RouterLink}
                to="/consultation"
              >
                BOOK NOW
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6} sx={{ 
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Box>
              <img 
                src={premium1} 
                alt="Premium Service" 
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: '300px',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Footer Component
const Footer = () => {
  return (
    <Box sx={{ bgcolor: '#000', color: 'white', pt: 5, pb: 3 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo and Description */}
          <Grid item xs={12} md={3}>
            <Box sx={{ mb: 2 }}>
              <img 
                src={logo0}
                alt="Warrili" 
                style={{ 
                  height: '35px',
                  filter: 'brightness(0) invert(1)',
                  opacity: 0.9,
                }} 
              />
              <Typography 
                variant="caption" 
                component="p" 
                sx={{ 
                  mt: 2,
                  color: '#999',
                  maxWidth: 250
                }}
              >
                Providing expert technical solutions and consultation services to help businesses overcome their IT challenges.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              {/* Social Media Icons */}
              <IconButton size="small" sx={{ color: '#999' }}>
                <FacebookIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: '#999' }}>
                <TwitterIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: '#999' }}>
                <LinkedInIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: '#999' }}>
                <InstagramIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Services
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link to="/tech-support" style={{ color: '#999', textDecoration: 'none' }}>Tech Support</Link>
              <Link to="/consultation" style={{ color: '#999', textDecoration: 'none' }}>Expert Consultation</Link>
              <Link to="/it-solutions" style={{ color: '#999', textDecoration: 'none' }}>IT Solutions</Link>
              <Link to="/remote-support" style={{ color: '#999', textDecoration: 'none' }}>Remote Support</Link>
              <Link to="/ai-services" style={{ color: '#999', textDecoration: 'none' }}>AI Services</Link>
            </Box>
          </Grid>

          {/* Company */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Company
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link to="/about" style={{ color: '#999', textDecoration: 'none' }}>About Us</Link>
              <Link to="/contact" style={{ color: '#999', textDecoration: 'none' }}>Contact</Link>
              <Link to="/careers" style={{ color: '#999', textDecoration: 'none' }}>Careers</Link>
              <Link to="/blog" style={{ color: '#999', textDecoration: 'none' }}>Blog</Link>
              <Link to="/faq" style={{ color: '#999', textDecoration: 'none' }}>FAQ</Link>
            </Box>
          </Grid>

          {/* Legal */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link to="/terms" style={{ color: '#999', textDecoration: 'none' }}>Terms of Service</Link>
              <Link to="/privacy" style={{ color: '#999', textDecoration: 'none' }}>Privacy Policy</Link>
              <Link to="/cookies" style={{ color: '#999', textDecoration: 'none' }}>Cookie Policy</Link>
              <Link to="/security" style={{ color: '#999', textDecoration: 'none' }}>Security</Link>
              <Link to="/compliance" style={{ color: '#999', textDecoration: 'none' }}>Compliance</Link>
            </Box>
          </Grid>
        </Grid>

        {/* Copyright */}
        <Box sx={{ 
          mt: 4, 
          pt: 3, 
          borderTop: '1px solid rgba(255,255,255,0.1)',
          textAlign: 'center',
          color: '#666',
          fontSize: '0.875rem'
        }}>
          © 2023 Warrili. All rights reserved.
        </Box>
      </Container>
    </Box>
  );
};

// Main component
const Under = () => {
  return (
    <Box>
      {/* Add top navigation links */}
      <TopNavigation />
      
      {/* Replace the first section with our cloned ServicesSection */}
      <ServicesSection />
      
      {/* ONLY ONE Call to Action Section */}
      <CallToActionSection />
      
      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Under; 